



























































































































import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { formatCurrency } from "@/lib/utility/currency-helper";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { IInvoiceLine } from "@/models/invoice-line.entity";
import { currencySymbolMap } from "@/store/mapper/currency-symbol.map";
import { mixins } from "vue-class-component";
import { Component, Prop, Watch, Ref } from "vue-property-decorator";
import RulesMixin from "@/mixins/RulesMixin.vue";
import { ExtendedLineItem } from "@/components/repair-estimate/LineItemsForm.vue";
import { IProduct, AccountingTypeEnum } from "@/models/product.entity";
import { PartnerModule } from "@/store/modules/partner";
import { ProductModule } from "@/store/modules/product.store";
import { debounce } from "debounce";
import { handleError } from "@/lib/utility/handleError";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { UserModule } from "@/store/modules/me-user.store";

@Component({
  components: {},
  filters: {}
})
export default class InvoiceFormLinesTableRow extends mixins(PermissionMixin, RulesMixin) {
  readonly formatCurrency = formatCurrency;

  readonly currencySymbolMap = currencySymbolMap;

  // Track validation state for each field
  validity: Record<string, boolean> = {
    title: true,
    amount: true,
    quantity: true,
    taxRate: true
  };

  @Prop()
  value!: ExtendedLineItem;

  @Prop()
  currency!: CurrencyEnum;

  @Prop()
  accountingType?: AccountingTypeEnum;

  @Prop()
  index!: number;

  @Prop({ default: true })
  readonly!: string;

  @Prop()
  isValid!: boolean;

  get canReadProduct() {
    return UserModule.abilities.can(ActionEnum.READ, ResourceEnum.PRODUCT, this.permissionPartnerId);
  }

  get products() {
    return ProductModule.entities;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get taxRates() {
    return this.partner.settings?.taxRates;
  }

  mounted() {
    this.$nextTick(() => {
      // Validate all fields on mount after DOM is ready
      this.validateAllFields();
    });
  }

  debounceTimeout = 300;

  debouncedProductSearch = debounce(
    (search: string, item?: ExtendedLineItem) => this.searchProduct(search, item),
    this.debounceTimeout,
    false
  );

  async searchProduct(search: string, item?: ExtendedLineItem) {
    if (!item || !search) {
      return;
    }

    item.search = search;
    item.isLoading = true;

    try {
      ProductModule.setSearch(item.search);
      await ProductModule.fetchPage({ partnerId: this.partner.id });
    } catch (e) {
      handleError(e);
    } finally {
      item.isLoading = false;
    }
  }

  onProductInput(e: IProduct) {
    this.value.product = e;
    this.addAsLineItem(this.value);
  }

  addAsLineItem(lineItem: ExtendedLineItem) {
    if (!lineItem || !lineItem.product) {
      return;
    }

    this.$log.debug(lineItem);

    const productLineItem = lineItem.product as IProduct;
    if (productLineItem.title) {
      lineItem.setFromProduct(productLineItem, this.accountingType);
    } else {
      lineItem.title = lineItem.product as string;
    }
  }

  /**
   * Validates a specific field and updates the validity state
   */
  validateField(fieldName: string) {
    // Get the rules for the field
    let fieldRules: any[] = [];

    switch (fieldName) {
      case "title":
        fieldRules = this.rules(this.RulesEnum.REQUIRED_RULE);
        break;
      case "amount":
        fieldRules = this.rules(this.RulesEnum.REQUIRED_RULE);
        break;
      case "quantity":
        fieldRules = this.rules(this.RulesEnum.REQUIRED_RULE, this.RulesEnum.NOT_NEGATIVE_RULE);
        break;
      case "taxRate":
        fieldRules = this.rules(
          this.RulesEnum.REQUIRED_RULE,
          this.RulesEnum.NOT_NEGATIVE_RULE,
          this.RulesEnum.MAX_RULE_100
        );
        break;
      default:
        fieldRules = [];
    }

    // Apply rules and check validity
    const value: IInvoiceLine[keyof IInvoiceLine] = this.value[fieldName as keyof IInvoiceLine];
    let isValid = true;

    for (const rule of fieldRules) {
      const result = rule(value);
      if (result !== true) {
        isValid = false;
        break;
      }
    }

    // Update validity state
    this.validity[fieldName] = isValid;
    this.updateRowValidity();
  }

  /**
   * Validates all fields in the row
   */
  validateAllFields() {
    this.validateField("title");
    this.validateField("amount");
    this.validateField("quantity");
    this.validateField("taxRate");
  }

  /**
   * Updates the overall row validity state and emits the change
   */
  updateRowValidity() {
    const rowIsValid = Object.values(this.validity).every(v => v === true);
    this.$emit("update:isValid", rowIsValid);
  }

  @Watch("value", { deep: true })
  onValueChanged() {
    // Re-validate when the value changes externally
    this.validateAllFields();
  }
}
